import { defineStore } from 'pinia';

export const useGlobalStore = defineStore('global', {
    state: () => ({
        requestParams: {},
        user: {},

        // Whether we should be showing the loading overlay or not
        showLoadingOverlay: false,
        showLoadingRequests: 0,

        helpLink: '',
        themeName: '',
        defaultLocale: 'en',

        darkMode: false,
        loadingText: null,

        mediaLoading: null,
        mediaRate: 100,
        mediaPitch: 0,
        menu: [],

        requests: {},
        routeData: {},

        // Whether we should be showing the status message or not
        showStatusMessage: false,

        // Text to display with the status message
        statusText: null,

        // type of the status message
        statusType: null,

        switchMode: false,
        systemUse: false,

        navigateOptions: null,
        exportOptions: null,

        lastMenuOption: null,
        menuOption: null,

        // Track user session
        userSessionDuration: 0,
        userSessionLastAccessed: null,
    }),
    getters: {
        hasRight: (state) => (right) => {
            return state.user.rights && state.user.rights.includes(right);
        },
        isLoading:
            (state) =>
            (target, params = {}) => {
                if (Object.keys(params).length === 0) {
                    for (let key of Object.keys(state.requests)) {
                        const parsed = JSON.parse(key);
                        if (parsed.target === target && !state.requests[key].complete) {
                            return true;
                        }
                    }
                }

                const requestKey = JSON.stringify({ target, params });
                return state.requests[requestKey] && !state.requests[requestKey].complete;
            },
    },
    actions: {
        addRequest(key) {
            this.requests[key] = { complete: false };
        },
        buildRouteStore(services, orgId) {
            this.routeData = {
                orgId: orgId ? orgId : this.user.orgId,
                canRecord: services.canRecord,
                loadedLists: [],
                mediaLists: services.mediaLists,
                splitProfiles: services.percentageSplitProfiles,
                scripts: services.autoAttendantProfiles
                    .map((profile) => ({ ...profile, allowed: true }))
                    .concat(services.scripts)
                    .sort((a, b) => (a.name > b.name ? 1 : -1)),
                treatments: services.treatments,
                trunks: services.trunks,
                users: services.users,
                voicemailProfiles: services.voicemailProfiles,
            };
        },
        clearRouteData() {
            this.routeData = {};
        },
        exportFile(options) {
            this.exportOptions = options;
        },
        navigate(options) {
            this.navigateOptions = options;
        },
        removeRequest(key) {
            if (this.requests[key]) {
                this.requests[key].complete = true;
            }
        },
        setHelpLinks({ helpLink, defaultLocale, themeName }) {
            this.helpLink = helpLink.replace('{server.name}', window.location.host);
            this.defaultLocale = defaultLocale;
            this.themeName = themeName;
        },

        setThemeName(themeName) {
            this.themeName = themeName;
        },

        setMediaLoading(type) {
            this.mediaLoading = type;
        },

        setMediaPitch(pitch) {
            this.mediaPitch = pitch;
        },

        setMediaRate(rate) {
            this.mediaRate = rate;
        },

        setRequestParams(requestParams) {
            this.requestParams = requestParams;
        },

        // Used when the application is started to set values used by any component that interacts with the server
        setUser(user) {
            this.user = user;
            this.securityToken = user.session.securityToken;
            this.requestParams = { ...this.requestParams, securityToken: this.securityToken };
        },

        setUserSessionDuration(seconds) {
            this.userSessionDuration = seconds;
        },

        setUserSessionLastAccessed(dateTime) {
            this.userSessionLastAccessed = dateTime;
        },

        setMenuOption(option) {
            this.menuOption = option;
            if (option) {
                this.lastMenuOption = option;
            }
        },

        // Mutators for loading state
        setShowLoadingOverlay(loadingText) {
            this.loadingText = loadingText;
            this.showLoadingOverlay = true;
        },
        hideLoadingOverlay() {
            this.showLoadingOverlay = false;
            this.loadingText = null;
        },
        setLoadingRequests(value) {
            this.showLoadingRequests = Math.max(this.showLoadingRequests + value, 0);
        },

        // Mutators for status message
        showSuccessMessage(statusText) {
            this.statusText = statusText;
            this.statusType = 'success';
            this.showStatusMessage = true;
        },
        showErrorMessage(statusText) {
            this.statusText = statusText;
            this.statusType = 'error';
            this.showStatusMessage = true;
        },
        hideStatusMessage() {
            this.showStatusMessage = false;
            this.statusText = null;
        },
    },
});
